import { CheckOutlined, HeartOutlined } from "@ant-design/icons"
import { Button, notification, Table, Tag, Tooltip } from "antd"
import React, { useMemo, useState } from "react"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { useJobseeker } from "../../hooks"
import { convertTimestampToYmdhm } from "../../lib/date"
import { companyState } from "../../recoils/companyState"

import type { Jobseeker, JobseekerId } from "../../interfaces/IJobseeker"
import type { ColumnsType } from "antd/lib/table"

const { success } = notification

type JobseekerListProps = {
  jobseekers: Jobseeker[]
}

type LoadingHash = { [key in JobseekerId]: boolean }

const JobseekerList = (props: JobseekerListProps): JSX.Element => {
  const company = useRecoilValue(companyState)
  const { postInterested } = useJobseeker()
  const [loadingHash, setLoadingHash] = useState<LoadingHash>({})

  const disabledVisitHistory = !company?.plan || company.plan < 1
  const disabledInterested = !company?.plan || company.plan < 2

  const calculateAge = (birthday: Date): string => {
    const today = new Date()

    const targetDate =
      today.getFullYear() * 10000 +
      (today.getMonth() + 1) * 100 +
      today.getDate()
    const birthdate =
      birthday.getFullYear() * 10000 +
      (birthday.getMonth() + 1) * 100 +
      birthday.getDate()

    const age = Math.floor((targetDate - birthdate) / 10000)
    return age > 0 ? age.toString() : "--"
  }

  const onSubmit = async (jobseeker: Jobseeker): Promise<void> => {
    const jobseekerId = jobseeker.id
    loadingHash[jobseekerId] = true
    setLoadingHash({ ...loadingHash })
    try {
      await postInterested({
        jobseekerId: jobseekerId,
        recruitId: jobseeker.recruitId,
      })
      success({ message: "気になる通知を送りました" })
    } catch (error) {
      console.error(error)
      notification.error({ message: "予期せぬエラーが発生しました" })
    } finally {
      loadingHash[jobseekerId] = false
      setLoadingHash({ ...loadingHash })
    }
  }

  const columns: ColumnsType<Jobseeker> = [
    {
      title: "",
      key: "isInterested",
      render: (_, jobseeker) => {
        if (disabledInterested) return <></>

        if (jobseeker.isInterested) {
          return (
            <Tooltip title="気になる通知を送信済み">
              <Button
                disabled
                type="primary"
                shape="circle"
                icon={<CheckOutlined />}
              />
            </Tooltip>
          )
        }

        return (
          <Tooltip title="気になる通知を送る">
            <Button
              danger
              shape="circle"
              loading={loadingHash[jobseeker.id]}
              icon={<HeartOutlined />}
              onClick={() => onSubmit(jobseeker)}
            />
          </Tooltip>
        )
      },
    },
    {
      title: "希望勤務地",
      dataIndex: "desiredPrefecture",
      key: "desiredPrefecture",
      sorter: (a, b) => {
        if ((a.desiredPrefecture as string) > (b.desiredPrefecture as string))
          return -1
        if ((a.desiredPrefecture as string) < (b.desiredPrefecture as string))
          return 1
        return 0
      },
    },
    {
      title: "年齢",
      dataIndex: "birthday",
      key: "id",
      render: (_, jobseeker) => {
        if (!jobseeker.birthday || !jobseeker.birthday._seconds) return <></>
        const birthday = new Date(jobseeker.birthday._seconds * 1000)
        return <div>{calculateAge(birthday)}</div>
      },
      sorter: (a, b) => {
        const ageA = calculateAge(new Date((a?.birthday?._seconds ?? 0) * 1000))
        const ageB = calculateAge(new Date((b?.birthday?._seconds ?? 0) * 1000))
        if (ageA > ageB) return -1
        if (ageA < ageB) return 1
        return 0
      },
    },
    {
      title: "希望船舶",
      dataIndex: "desiredShip",
      key: "desiredShip",
      sorter: (a, b) => {
        if ((a.desiredShip as string) > (b.desiredShip as string)) return -1
        if ((a.desiredShip as string) < (b.desiredShip as string)) return 1
        return 0
      },
    },
    {
      title: "希望部署",
      dataIndex: "desiredDepartment",
      key: "desiredDepartment",
    },
    {
      title: "乗船履歴",
      dataIndex: "boardingHistory",
      key: "boardingHistory",
      sorter: (a, b) => {
        if ((a.desiredShip as string) > (b.desiredShip as string)) return -1
        if ((a.desiredShip as string) < (b.desiredShip as string)) return 1
        return 0
      },
    },
    {
      title: "希望月収",
      dataIndex: "desiredSalary",
      key: "desiredSalary",
      render: (_, jobseeker) => {
        const desiredSalary = parseInt(jobseeker.desiredSalary ?? "")
        if (!desiredSalary) return <></>

        if (desiredSalary >= 10 && desiredSalary <= 100) {
          return <>{jobseeker.desiredSalary} 万円</>
        }

        return <>{jobseeker.desiredSalary}</>
      },
    },
    {
      title: "取得免状",
      key: "licenses",
      dataIndex: "id",
      render: (_, jobseeker) => (
        <>
          {jobseeker.licenses?.map((tag) => {
            return (
              <Tag color="geekblue" key={tag + jobseeker.id}>
                {tag}
              </Tag>
            )
          })}
        </>
      ),
    },
    {
      title: "閲覧日",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, jobseeker) => {
        return <div>{convertTimestampToYmdhm(jobseeker.createdAt)}</div>
      },
    },
  ]

  const styles = useMemo(() => {
    if (!disabledVisitHistory) {
      return {}
    }

    return { filter: "blur(5px)" }
  }, [props.jobseekers])

  return (
    <Container>
      <Table columns={columns} dataSource={props.jobseekers} style={styles} />
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  width: 60rem;
`

export default JobseekerList
